import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { compose } from 'ramda';
import styled from 'styled-components/macro';
import Grid from '@material-ui/core/Grid';
import { showToast, BackTo, Typography, Modal } from '@deeptrue-frontend/ui';

import { Dashboard } from 'components/Layout';
import { saveAsFile } from 'utils/exportToCsv';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { ProjectType } from 'store/modules/projects/projects.types';
import { OrganizationType } from 'store/modules/auth/auth';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import api from 'api/api';
import Forms from 'components/Forms';
import { useFormatMessage } from 'components/IntlProvider';
import { withProject } from '../../withProject';
import ProjectSendInvitations from './ProjectSendInvitations';

const { connect, selectors } = StoreProvider;

const StyledModal = styled(Modal)`
  max-width: 90%;
  max-height: 60%;
  min-height: 10rem;
`;

const Header = styled(Typography)`
  font-size: 1.25rem;
`;

const Subtitle = styled(Typography)`
  font-size: 0.875rem;
`;

interface ProjectSurveyProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  project: ProjectType;
  organization: OrganizationType;
  updateProject: () => void;
}

const LinkDataCollection = ({
  project,
  organization,
  location,
  match: { params },
  history,
  updateProject,
}: ProjectSurveyProps) => {
  const [surveyUiid, setSurveyUiid] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState('');
  const [areAvailableSurveys, setAvailableSurveys] = useState(false);
  const t = useFormatMessage();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isReminderChecked, setIsReminderChecked] = useState<
    string | undefined
  >(project.send_reminders);
  const [isSendInitationModalOpen, setSendInvitationModalOpen] = useState(
    false
  );
  const [respondentsList, setRespondentsList] = useState({
    count: 0,
    results: [],
    next: null,
  });

  useEffect(() => {
    setIsReminderChecked(project.send_reminders ? 'trye' : undefined);
  }, [project.send_reminders]);

  useEffect(() => {
    if (project.id !== -1) {
      api.resources
        .getProjectSurveys({
          orgName: params.codename || organization.codename,
          uuid: project.uuid,
        })
        .then(({ data }) => {
          if (data.results.length) {
            const { uuid } = data.results[0];

            setSurveyUiid(uuid);
            setAvailableSurveys(true);
          }
        })
        .catch(() => {
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  useEffect(() => {
    if (surveyUiid) {
      api.resources
        .getRespondentsList({
          uuid: surveyUiid,
          parsedSearch: { ordering: '-Created at', limit: 1 },
        })
        .then(({ data }: any) => {
          if (data) {
            setRespondentsList({
              ...data,
              results: [...respondentsList.results, ...data.results],
            });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyUiid]);

  const downloadCSV = ({
    country,
    count,
    parameters,
  }: {
    country: string;
    count: number;
    parameters?: { [k: string]: string };
  }) => {
    return api.resources
      .generateSurveyUrls({
        orgName: params.codename || organization.codename,
        uuid: surveyUiid || '',
        country: country.toString(),
        count: Number(count),
        parameters,
      })
      .then(({ data }) => {
        updateProject();
        return saveAsFile(data, 'surveys.csv');
      })
      .catch(({ response }) => {
        /* eslint-disable-next-line */
        const briefingWarnError = response!.data!.non_field_errors;

        if (briefingWarnError) {
          setSubmitError(briefingWarnError);

          showToast({
            type: 'warn',
            title: t(
              'errors.toast.briefingConfirm',
              'Briefing needs to be confirmed to perform this action.'
            ),
          });
        } else {
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
        }
      });
  };
  const { final_fee } = project;

  const backLink = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data`
    : `/project/${project.id}/collect-data`;

  return (
    <Dashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
    >
      <StyledModal
        isVisible={isSendInitationModalOpen}
        onBackdropClick={() => setSendInvitationModalOpen(false)}
        onCloseClick={() => setSendInvitationModalOpen(false)}
      >
        <ProjectSendInvitations
          location={location}
          params={params}
          credits={final_fee}
          voucherInfo={project.voucher}
          surveyUiid={surveyUiid}
          history={history}
          closeModal={() => setSendInvitationModalOpen(false)}
          updateProject={updateProject}
        />
      </StyledModal>
      <BackTo onClick={() => history.push(backLink)}>
        {t('project.collectData.backTo', 'Back to collect data')}
      </BackTo>

      {areAvailableSurveys ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Header variant="h5">
              {t('project.collectData.link.title', 'By Link')}
            </Header>
            <Subtitle mb="1.5rem">
              {t(
                'project.collectData.link.subtitle',
                'Generate survey links to share them with your contacts.'
              )}
            </Subtitle>
            <Forms.DownloadSurveyUrls
              project={project}
              onSubmit={downloadCSV}
              submitError={submitError}
              initialValues={{ country: 'gb', count: 20 }}
            />
          </Grid>
        </Grid>
      ) : (
        <Typography variant="copy1">
          {t('pages.project.nosurveys', 'No surveys available')}
        </Typography>
      )}
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
  })),
  withMetaDescription({ title: 'Dashboard projects' }),
  withProject(({ match, organization }: ProjectSurveyProps) => ({
    projectId: match.params.id,
    organizationCodename: match.params.codename || organization.codename,
  }))
)(LinkDataCollection);
